import { ApolloQueryResult } from "@apollo/client";
import { PostCodeFilterInput } from "@the-volte/svc-core-sdk";
import {
  ProductList,
  ProductList_products,
} from "@the-volte/svc-core-sdk/lib/queries/gqlTypes/ProductList";
import { useRouter } from "next/router";
import { useState } from "react";

import { ssrMode } from "@app/constants";
import { RequireOnlyOne } from "@app/core/tsUtils";
import { ProductList_products as ProductList_products_legacy } from "@app/views/gqlTypes/ProductList";
import { useProductsQuery } from "@app/views/queries";
import { IFilters } from "@types";
import { getBrowserPath } from "@utils/filters";

interface ProductLoadingInputType {
  ssrData;
  filters: IFilters;
  clientFiltered: boolean;
  hasDeliveryFilter: boolean;
  ids: RequireOnlyOne<{
    categoryId: string | undefined;
    collectionId: string | undefined;
  }>;
  postcodes?: PostCodeFilterInput[];
  lenderSlug?: string;
  queryType?: string;
}

export const useProductLoading = ({
  ssrData,
  filters,
  ids,
  clientFiltered,
  hasDeliveryFilter,
  postcodes,
  lenderSlug,
  queryType,
}: ProductLoadingInputType) => {
  const router = useRouter();
  const [loadMoreError, setLoadMoreError] = useState(false);

  let loadMore = () => {};
  let loading = false;

  // For SSR this comes from getServerSideProps
  let clientData:
    | ProductList_products
    | ProductList_products_legacy
    | null
    | undefined = ssrData;

  // The SSR data can get stale e.g. when going back from a product detail
  // Although in this case a SSR is performed, next.js will miss params
  // that were added an the SSR path gets out of sync with client path
  const ssrDataIsStale =
    !ssrMode &&
    (getBrowserPath(router) === router.asPath ||
      hasDeliveryFilter ||
      clientFiltered);

  if (!ssrMode) {
    // Query products but we don't use the result on initial client render
    const query = useProductsQuery(
      filters,
      ids,
      ssrDataIsStale ? undefined : clientData?.pageInfo?.endCursor || undefined,
      postcodes || [],
      lenderSlug || "",
      queryType
    );

    clientData = query.data?.products;
    loadMore = query.loadMore;
    loading = query.loading;
  }

  const products = clientData?.edges?.map((e) => e.node) || [];
  const pageInfo = clientData?.pageInfo;
  const numberOfProducts = clientData?.totalCount || 0;

  const handleLoadMore = async () => {
    if (!loadMore) return;
    const loadMoreResult: ApolloQueryResult<ProductList> = await loadMore(
      (prev, next) => ({
        products: {
          ...prev.products,
          edges: [
            ...(prev.products?.edges || []),
            ...(next.products?.edges || []),
          ],
          pageInfo: next.products?.pageInfo,
        },
      }),
      pageInfo?.endCursor
    );
    if (loadMoreResult?.error?.networkError) {
      setLoadMoreError(true);
    }
  };

  return {
    products,
    loading,
    pageInfo,
    numberOfProducts,
    handleLoadMore,
    loadMoreError,
  };
};
